// Stage 73.20cp (Phase B) — Statutory State/District Rule Master admin UI.
//
// Renders three tabs:
//   1. States — CRUD state_master
//   2. Districts — CRUD district_master (filtered by selected state)
//   3. Rules — CRUD statutory_rule_master (per state, optionally
//      per district, with PT + LWF config, effective dates)
//
// Route: /admin/statutory-masters
// RBAC (backend enforced): admin roles only.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { Plus, Trash2, Edit3, MapPin, Building2, ScrollText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyState = { name: '', code: '', country: 'India', is_active: true };
const emptyDistrict = { state_id: '', name: '', code: '', is_active: true };
const emptyRule = {
  state_id: '', district_id: '',
  effective_from: '', effective_to: '', is_active: true,
  pt_config: {
    applicable: true, slab_basis: 'monthly_gross', deduction_method: 'monthly',
    slab_months: [], slabs: [{ from_amount: 0, to_amount: null, amount: 0 }],
  },
  lwf_config: {
    applicable: true, periodicity: 'monthly', due_months: [],
    employee_amount: 0, employer_amount: 0,
  },
  notes: '',
};

export default function StatutoryMasters() {
  const [tab, setTab] = useState('states');
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [rules, setRules] = useState([]);
  const [selStateId, setSelStateId] = useState('');

  const [stateForm, setStateForm] = useState(emptyState);
  const [districtForm, setDistrictForm] = useState(emptyDistrict);
  const [ruleForm, setRuleForm] = useState(emptyRule);
  const [editingStateId, setEditingStateId] = useState(null);
  const [editingDistrictId, setEditingDistrictId] = useState(null);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchStates = useCallback(async () => {
    const r = await axios.get(`${API_URL}/statutory-masters/states?active_only=false`, { withCredentials: true });
    setStates(r.data || []);
  }, []);
  const fetchDistricts = useCallback(async (sid) => {
    const url = sid
      ? `${API_URL}/statutory-masters/districts?state_id=${sid}&active_only=false`
      : `${API_URL}/statutory-masters/districts?active_only=false`;
    const r = await axios.get(url, { withCredentials: true });
    setDistricts(r.data || []);
  }, []);
  const fetchRules = useCallback(async (sid) => {
    const url = sid
      ? `${API_URL}/statutory-masters/rules?state_id=${sid}&active_only=false`
      : `${API_URL}/statutory-masters/rules?active_only=false`;
    const r = await axios.get(url, { withCredentials: true });
    setRules(r.data || []);
  }, []);

  useEffect(() => { fetchStates(); }, [fetchStates]);
  useEffect(() => { fetchDistricts(selStateId); }, [selStateId, fetchDistricts]);
  useEffect(() => { fetchRules(selStateId); }, [selStateId, fetchRules]);

  const districtsForState = useMemo(
    () => districts.filter((d) => !ruleForm.state_id || d.state_id === ruleForm.state_id),
    [districts, ruleForm.state_id],
  );

  // ── STATE handlers ──
  const saveState = async () => {
    setSaving(true);
    try {
      if (editingStateId) {
        await axios.put(`${API_URL}/statutory-masters/states/${editingStateId}`, stateForm, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/statutory-masters/states`, stateForm, { withCredentials: true });
      }
      setStateForm(emptyState);
      setEditingStateId(null);
      await fetchStates();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save state'); }
    setSaving(false);
  };
  const editState = (s) => { setStateForm({ name: s.name, code: s.code || '', country: s.country || 'India', is_active: s.is_active !== false }); setEditingStateId(s.id); };
  const deleteState = async (s) => {
    if (!window.confirm(`Delete state "${s.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/statutory-masters/states/${s.id}`, { withCredentials: true });
      await fetchStates();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete state');
    }
  };

  // ── DISTRICT handlers ──
  const saveDistrict = async () => {
    setSaving(true);
    try {
      if (editingDistrictId) {
        await axios.put(`${API_URL}/statutory-masters/districts/${editingDistrictId}`, districtForm, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/statutory-masters/districts`, districtForm, { withCredentials: true });
      }
      setDistrictForm(emptyDistrict);
      setEditingDistrictId(null);
      await fetchDistricts(selStateId);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save district'); }
    setSaving(false);
  };
  const editDistrict = (d) => { setDistrictForm({ state_id: d.state_id, name: d.name, code: d.code || '', is_active: d.is_active !== false }); setEditingDistrictId(d.id); };
  const deleteDistrict = async (d) => {
    if (!window.confirm(`Delete district "${d.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/statutory-masters/districts/${d.id}`, { withCredentials: true });
      await fetchDistricts(selStateId);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete district');
    }
  };

  // ── RULE handlers ──
  const addSlab = () => setRuleForm({
    ...ruleForm,
    pt_config: {
      ...ruleForm.pt_config,
      slabs: [...ruleForm.pt_config.slabs, { from_amount: 0, to_amount: null, amount: 0 }],
    },
  });
  const updateSlab = (i, key, val) => {
    const slabs = [...ruleForm.pt_config.slabs];
    slabs[i] = { ...slabs[i], [key]: val === '' ? null : Number(val) };
    setRuleForm({ ...ruleForm, pt_config: { ...ruleForm.pt_config, slabs } });
  };
  const removeSlab = (i) => setRuleForm({
    ...ruleForm,
    pt_config: {
      ...ruleForm.pt_config,
      slabs: ruleForm.pt_config.slabs.filter((_, ix) => ix !== i),
    },
  });
  const saveRule = async () => {
    setSaving(true);
    try {
      const payload = {
        ...ruleForm,
        district_id: ruleForm.district_id || null,
        effective_from: ruleForm.effective_from || null,
        effective_to: ruleForm.effective_to || null,
      };
      if (editingRuleId) {
        await axios.put(`${API_URL}/statutory-masters/rules/${editingRuleId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/statutory-masters/rules`, payload, { withCredentials: true });
      }
      setRuleForm(emptyRule);
      setEditingRuleId(null);
      await fetchRules(selStateId);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save rule'); }
    setSaving(false);
  };
  const editRule = (r) => { setRuleForm({ ...emptyRule, ...r }); setEditingRuleId(r.id); };
  const deleteRule = async (r) => {
    if (!window.confirm(`Deactivate rule for ${r.state_name}${r.district_name ? ` / ${r.district_name}` : ''}?`)) return;
    await axios.delete(`${API_URL}/statutory-masters/rules/${r.id}`, { withCredentials: true });
    await fetchRules(selStateId);
  };

  return (
    <MainLayout>
    <div data-testid="statutory-masters-page" className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Statutory Rule Masters</h1>
        <p className="text-sm text-slate-500 mt-1">
          Dynamic State/District PT &amp; LWF rules. During payroll processing the engine
          resolves the most-specific active rule for each employee&apos;s work location.
        </p>
      </div>

      <div className="border-b border-slate-200 flex space-x-1">
        {[
          { key: 'states',    label: 'States',    icon: MapPin,    testid: 'tab-states' },
          { key: 'districts', label: 'Districts', icon: Building2, testid: 'tab-districts' },
          { key: 'rules',     label: 'Rules',     icon: ScrollText, testid: 'tab-rules' },
        ].map((t) => (
          <button key={t.key} data-testid={t.testid}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center space-x-1.5 ${
                    tab === t.key ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-slate-600 hover:text-slate-800'
                  }`}>
            <t.icon className="w-4 h-4" />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* STATES */}
      {tab === 'states' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs text-slate-500 mb-1">Name</label>
              <input data-testid="state-name-input" value={stateForm.name} onChange={(e) => setStateForm({ ...stateForm, name: e.target.value })}
                     className="w-full border rounded px-3 py-1.5 text-sm" placeholder="e.g. Tamil Nadu" />
            </div>
            <div className="w-32">
              <label className="block text-xs text-slate-500 mb-1">Code</label>
              <input data-testid="state-code-input" value={stateForm.code} onChange={(e) => setStateForm({ ...stateForm, code: e.target.value })}
                     className="w-full border rounded px-3 py-1.5 text-sm" placeholder="TN" />
            </div>
            <button data-testid="save-state-btn" onClick={saveState} disabled={saving || !stateForm.name}
                    className="bg-[#0052FF] disabled:bg-slate-300 text-white px-4 py-1.5 rounded text-sm flex items-center space-x-1">
              <Plus className="w-4 h-4" /><span>{editingStateId ? 'Update' : 'Add'}</span>
            </button>
            {editingStateId && <button className="text-sm text-slate-500" onClick={() => { setEditingStateId(null); setStateForm(emptyState); }}>Cancel</button>}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {states.map((s) => (
              <div key={s.id} data-testid={`state-row-${s.id}`} className="flex items-center justify-between p-3 hover:bg-slate-50">
                <div className="text-sm">
                  <span className="font-medium text-slate-800">{s.name}</span>
                  {s.code && <span className="ml-2 text-xs text-slate-500">({s.code})</span>}
                  {!s.is_active && <span className="ml-2 px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">Inactive</span>}
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => editState(s)} className="p-1.5 hover:bg-slate-100 rounded"><Edit3 className="w-4 h-4 text-slate-600" /></button>
                  <button onClick={() => deleteState(s)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              </div>
            ))}
            {states.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No states — add one above.</p>}
          </div>
        </div>
      )}

      {/* DISTRICTS */}
      {tab === 'districts' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-4 gap-2 items-end">
            <div>
              <label className="block text-xs text-slate-500 mb-1">State</label>
              <select data-testid="district-state-select" value={districtForm.state_id}
                      onChange={(e) => setDistrictForm({ ...districtForm, state_id: e.target.value })}
                      className="w-full border rounded px-3 py-1.5 text-sm">
                <option value="">Select state…</option>
                {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">District Name</label>
              <input data-testid="district-name-input" value={districtForm.name}
                     onChange={(e) => setDistrictForm({ ...districtForm, name: e.target.value })}
                     className="w-full border rounded px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Code</label>
              <input value={districtForm.code} onChange={(e) => setDistrictForm({ ...districtForm, code: e.target.value })}
                     className="w-full border rounded px-3 py-1.5 text-sm" />
            </div>
            <button data-testid="save-district-btn" onClick={saveDistrict} disabled={saving || !districtForm.state_id || !districtForm.name}
                    className="bg-[#0052FF] disabled:bg-slate-300 text-white px-4 py-1.5 rounded text-sm flex items-center justify-center space-x-1 self-end">
              <Plus className="w-4 h-4" /><span>{editingDistrictId ? 'Update' : 'Add'}</span>
            </button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
            <label className="text-xs text-slate-500 mr-2">Filter by state</label>
            <select value={selStateId} onChange={(e) => setSelStateId(e.target.value)} className="text-sm border rounded px-3 py-1">
              <option value="">All</option>
              {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {districts.map((d) => (
              <div key={d.id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                <div className="text-sm">
                  <span className="font-medium text-slate-800">{d.name}</span>
                  {d.code && <span className="ml-2 text-xs text-slate-500">({d.code})</span>}
                  <span className="ml-2 text-xs text-slate-500">· {states.find((s) => s.id === d.state_id)?.name || 'Unknown'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => editDistrict(d)} className="p-1.5 hover:bg-slate-100 rounded"><Edit3 className="w-4 h-4 text-slate-600" /></button>
                  <button onClick={() => deleteDistrict(d)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              </div>
            ))}
            {districts.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No districts.</p>}
          </div>
        </div>
      )}

      {/* RULES */}
      {tab === 'rules' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
            <h3 className="text-sm font-semibold text-slate-800">{editingRuleId ? 'Edit Rule' : 'New Rule'}</h3>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">State*</label>
                <select data-testid="rule-state-select" value={ruleForm.state_id}
                        onChange={(e) => setRuleForm({ ...ruleForm, state_id: e.target.value, district_id: '' })}
                        className="w-full border rounded px-3 py-1.5 text-sm">
                  <option value="">Select…</option>
                  {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">District (optional — state-wide if empty)</label>
                <select value={ruleForm.district_id || ''} onChange={(e) => setRuleForm({ ...ruleForm, district_id: e.target.value })}
                        className="w-full border rounded px-3 py-1.5 text-sm">
                  <option value="">— State-wide —</option>
                  {districtsForState.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Effective From</label>
                <input type="date" value={ruleForm.effective_from || ''} onChange={(e) => setRuleForm({ ...ruleForm, effective_from: e.target.value })}
                       className="w-full border rounded px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Effective To</label>
                <input type="date" value={ruleForm.effective_to || ''} onChange={(e) => setRuleForm({ ...ruleForm, effective_to: e.target.value })}
                       className="w-full border rounded px-3 py-1.5 text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-800">Professional Tax</h4>
                  <label className="flex items-center text-xs">
                    <input type="checkbox" checked={ruleForm.pt_config.applicable}
                           onChange={(e) => setRuleForm({ ...ruleForm, pt_config: { ...ruleForm.pt_config, applicable: e.target.checked } })}
                           className="mr-1" /> Applicable
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select value={ruleForm.pt_config.slab_basis}
                          onChange={(e) => setRuleForm({ ...ruleForm, pt_config: { ...ruleForm.pt_config, slab_basis: e.target.value } })}
                          className="border rounded px-2 py-1 text-xs">
                    <option value="monthly_gross">Monthly Gross</option>
                    <option value="halfyearly_gross">Half-Yearly Gross</option>
                    <option value="annual_gross">Annual Gross</option>
                    <option value="half_year_slab_spread">Half-Year Slab Spread</option>
                  </select>
                  <select value={ruleForm.pt_config.deduction_method}
                          onChange={(e) => setRuleForm({ ...ruleForm, pt_config: { ...ruleForm.pt_config, deduction_method: e.target.value } })}
                          className="border rounded px-2 py-1 text-xs">
                    <option value="monthly">Monthly</option>
                    <option value="half_yearly">Half-Yearly</option>
                    <option value="monthly_spread">Monthly Spread</option>
                    <option value="immediate_slab">Immediate Slab</option>
                    <option value="half_year_slab_spread">Half-Year Slab Spread</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Slabs</label>
                  {ruleForm.pt_config.slabs.map((sl, i) => (
                    <div key={i} className="grid grid-cols-4 gap-1 mb-1">
                      <input type="number" value={sl.from_amount} onChange={(e) => updateSlab(i, 'from_amount', e.target.value)}
                             placeholder="From" className="border rounded px-2 py-1 text-xs" />
                      <input type="number" value={sl.to_amount ?? ''} onChange={(e) => updateSlab(i, 'to_amount', e.target.value)}
                             placeholder="To (blank = ∞)" className="border rounded px-2 py-1 text-xs" />
                      <input type="number" value={sl.amount} onChange={(e) => updateSlab(i, 'amount', e.target.value)}
                             placeholder="Amount" className="border rounded px-2 py-1 text-xs" />
                      <button onClick={() => removeSlab(i)} className="text-xs text-red-600 hover:bg-red-50 rounded">✕</button>
                    </div>
                  ))}
                  <button onClick={addSlab} className="text-xs text-[#0052FF] hover:underline mt-1">+ Add slab</button>
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-slate-800">Labour Welfare Fund</h4>
                  <label className="flex items-center text-xs">
                    <input type="checkbox" checked={ruleForm.lwf_config.applicable}
                           onChange={(e) => setRuleForm({ ...ruleForm, lwf_config: { ...ruleForm.lwf_config, applicable: e.target.checked } })}
                           className="mr-1" /> Applicable
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select value={ruleForm.lwf_config.periodicity}
                          onChange={(e) => setRuleForm({ ...ruleForm, lwf_config: { ...ruleForm.lwf_config, periodicity: e.target.value } })}
                          className="border rounded px-2 py-1 text-xs">
                    <option value="monthly">Monthly</option>
                    <option value="half_yearly">Half-Yearly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <input value={(ruleForm.lwf_config.due_months || []).join(',')}
                         onChange={(e) => setRuleForm({ ...ruleForm, lwf_config: { ...ruleForm.lwf_config, due_months: e.target.value.split(',').map((v) => parseInt(v)).filter(Boolean) } })}
                         placeholder="Due months (e.g. 6,12)" className="border rounded px-2 py-1 text-xs" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-slate-500">Employee Amount</label>
                    <input type="number" value={ruleForm.lwf_config.employee_amount}
                           onChange={(e) => setRuleForm({ ...ruleForm, lwf_config: { ...ruleForm.lwf_config, employee_amount: Number(e.target.value) } })}
                           className="w-full border rounded px-2 py-1 text-xs" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500">Employer Amount</label>
                    <input type="number" value={ruleForm.lwf_config.employer_amount}
                           onChange={(e) => setRuleForm({ ...ruleForm, lwf_config: { ...ruleForm.lwf_config, employer_amount: Number(e.target.value) } })}
                           className="w-full border rounded px-2 py-1 text-xs" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-2">
              <button data-testid="save-rule-btn" onClick={saveRule} disabled={saving || !ruleForm.state_id}
                      className="bg-[#0052FF] disabled:bg-slate-300 text-white px-4 py-1.5 rounded text-sm">
                {editingRuleId ? 'Update Rule' : 'Save Rule'}
              </button>
              {editingRuleId && <button onClick={() => { setEditingRuleId(null); setRuleForm(emptyRule); }} className="text-sm text-slate-500">Cancel</button>}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {rules.map((r) => (
              <div key={r.id} data-testid={`rule-row-${r.id}`} className="p-3 hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-medium text-slate-800">{r.state_name}</span>
                    {r.district_name && <span className="text-slate-500"> · {r.district_name}</span>}
                    {!r.is_active && <span className="ml-2 px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">Inactive</span>}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button onClick={() => editRule(r)} className="p-1.5 hover:bg-slate-100 rounded"><Edit3 className="w-4 h-4 text-slate-600" /></button>
                    <button onClick={() => deleteRule(r)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-1 space-x-3">
                  <span>PT: {r.pt_config?.applicable ? `${r.pt_config?.deduction_method} · ${(r.pt_config?.slabs || []).length} slabs` : 'off'}</span>
                  <span>LWF: {r.lwf_config?.applicable ? `${r.lwf_config?.periodicity} · Emp ₹${r.lwf_config?.employee_amount} / Empr ₹${r.lwf_config?.employer_amount}` : 'off'}</span>
                  <span>Effective: {r.effective_from || '—'} → {r.effective_to || '—'}</span>
                </div>
              </div>
            ))}
            {rules.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No rules yet.</p>}
          </div>
        </div>
      )}
    </div>
    </MainLayout>
  );
}
